import Box from "@mui/material/Box";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Services } from "assets/SideBarIcons/services.svg";
import StyledInput from "common/SearchField/Search";
import {
  useAssignAllVehicleTypeService,
  useAssignVehicleTypeService,
  useGetServiceVehicleTypeService,
  useUnassignAllVehicleTypeService,
  useUnassignVehicleTypeService,
} from "subModule/src/services/service/useService";
import ServiceTabs from "./ServiceTabs";
import { Grid } from "@mui/material";
import { ServiceProfileService } from "subModule/src/core/services";
import {
  GetServiceByIdResponse,
  ServiceResponse,
} from "subModule/src/services/service/interface";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";
import { pickProfileEntityTitle } from "Helpers/pickProfileEntityTitle";

export default function ServiceVehicleTypesTable() {
  const params = useParams();
  const serviceId = params?.id;
  const navigate = useNavigate();
  const [serviceTitle, setServiceTitle] = useState("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<any>("");
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [service, setService] = useState<ServiceResponse>({});
  const {
    data: tableData,
    isFetching,
    refetch,
  } = searchTerm
    ? useGetServiceVehicleTypeService(
        { pageNumber: pageNo, size: pageSize, title: searchTerm },
        +(params as any).id,
      )
    : useGetServiceVehicleTypeService(
        { pageNumber: pageNo, size: pageSize },
        +(params as any).id,
      );
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  let allVehicleTypesAssigned: unknown = tableData?.pages[0].data?.content
    .map((vehicleType) => vehicleType.linked)
    .every((linked) => linked === true);
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (Object.keys(service)?.length > 0) {
      fetchData();
    }
  }, [pageNo, pageSize, searchTerm, service]);
  useEffect(() => {
    getByID();
  }, []);
  const getByID = () => {
    ServiceProfileService.getServiceById(+(params as any)?.id)
      .then((res: GetServiceByIdResponse) => {
        if (res.success) {
          setService(res?.data);
          const name = pickProfileEntityTitle(res);
          if (name) setServiceTitle(name);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const clearSearch = () => {
    setSearch("");
    setSearchTerm("");
    if (searchTerm) {
      handleSearch("");
    }
  };
  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const handleSearchClick = () => {
    const trimmedSearch = search.trim();
    if (trimmedSearch !== "") handleSearch(trimmedSearch);
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value.trim());
    if (!event.target.value.trim()) handleSearch("");
  };
  const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
    setLoading(true);
    if (isChecked) {
      onAssignVehicleType({
        serviceId: +(params as any).id,
        vehicletypeId: row.id,
      });
    } else {
      onUnassignVehicleType({
        serviceId: +(params as any).id,
        vehicletypeId: row.id,
      });
    }
  };
  const assignUnassignAllEntities = (isChecked: boolean) => {
    setLoading(true);
    if (isChecked) {
      onAssignAllVehicleTypes({ serviceId: +(params as any).id });
    } else {
      onUnassignAllVehicleTypes({ serviceId: +(params as any).id });
    }
  };
  const { onAssignVehicleType } = useAssignVehicleTypeService({
    onSuccess: (msg) => {
      const response: any = {};
      Object.keys(msg).forEach((key, index) => {
        response[key] = Object.values(msg)[index];
      });
      if (response.success) {
        if (
          tableData?.pages[0].data.content.length ===
          tableData?.pages[0].data.content.filter(
            (vehicleType) => vehicleType.linked,
          ).length
        ) {
          allVehicleTypesAssigned = true;
        }
        refetch();
        setLoading(false);
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={
              t("vehicleType") + " " + t("assigned") + " " + t("successfully")
            }
          />,
          { variant: "success" },
        );
      } else {
        refetch();
        setLoading(false);
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    },
    onError: (err: any) => {
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      if (err instanceof Error) {
        console.error(err.stack);
      }
    },
  });
  const { onUnassignVehicleType } = useUnassignVehicleTypeService({
    onSuccess: (data) => {
      const response: any = {};
      Object.keys(data).forEach((key, index) => {
        response[key] = Object.values(data)[index];
      });
      if (response.success) {
        if (
          tableData?.pages[0].data.content.length !==
          tableData?.pages[0].data.content.filter(
            (vehicleType) => vehicleType.linked,
          ).length
        ) {
          allVehicleTypesAssigned = false;
        }
        refetch();
        setLoading(false);
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={
              t("vehicleType") + " " + t("unassigned") + " " + t("successfully")
            }
          />,
          { variant: "success" },
        );
      } else {
        refetch();
        setLoading(false);
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    },
    onError: (err: any) => {
      console.log("err====,  ", err);
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
    },
  });
  const { onAssignAllVehicleTypes } = useAssignAllVehicleTypeService({
    onSuccess: (msg) => {
      const response: any = {};
      Object.keys(msg).forEach((key, index) => {
        response[key] = Object.values(msg)[index];
      });
      if (response.success) {
        allVehicleTypesAssigned = true;
        refetch();
        setLoading(false);
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={
              t("All") +
              " " +
              t("vehicleTypes") +
              " " +
              t("assigned") +
              " " +
              t("successfully")
            }
          />,
          { variant: "success" },
        );
      } else {
        refetch();
        setLoading(false);
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    },
    onError: (err: any) => {
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      if (err instanceof Error) {
        console.error(err.stack);
      }
    },
  });
  const { onUnassignAllVehicleTypes } = useUnassignAllVehicleTypeService({
    onSuccess: (data) => {
      const response: any = {};
      Object.keys(data).forEach((key, index) => {
        response[key] = Object.values(data)[index];
      });
      if (response.success) {
        allVehicleTypesAssigned = false;
        refetch();
        setLoading(false);
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={
              t("All") +
              " " +
              t("vehicleTypes") +
              " " +
              t("unassigned") +
              " " +
              t("successfully")
            }
          />,
          { variant: "success" },
        );
      } else {
        refetch();
        setLoading(false);
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    },
    onError: (err: any) => {
      console.log("err====,  ", err);
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
    },
  });

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { labelKey: "Items" },
      { labelKey: "services", href: "/services" },
      {
        labelKey: "title",
        ...(serviceTitle ? { label: serviceTitle } : {}),
        href: serviceId ? `/service/${serviceId}` : undefined,
      },
      { labelKey: "assignVehicleType" },
    ],
    [serviceId, serviceTitle],
  );

  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/services")}
        heading={t("services")}
        icon={<Services height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
        breadcrumbItems={breadcrumbItems}
      />
      <Box>
        <div className="flex">
          {params?.id ? (
            <ServiceTabs params={params} selectedIndex={2} formData={service} />
          ) : null}
          <BackendPaginationTable
            columns={[{ id: "title", label: "title", numeric: false }]}
            data={tableData?.pages[0]?.data?.content || []}
            pageNo={pageNo}
            pageSize={pageSize}
            tableData={tableData?.pages[0]?.data}
            showCheckbox={true}
            showActions={true}
            enableSorting={true}
            showHeader={true}
            showSearch={true}
            showFilter={true}
            showCreateButton={false}
            actions={[]}
            handleChangePage={handleChangePage}
            handleChangeRows={handleChangeRows}
            assignUnassignSingleEntity={assignUnassignSingleEntity}
            assignUnassignAllEntities={assignUnassignAllEntities}
            allEntitiesAssigned={allVehicleTypesAssigned}
            loadingSearch={isFetching || loading}
            assignPrivilege={checkPrivileges("ROLE_SERVICE_VEHICLETYPE_ASSIGN")}
            unassignPrivilege={checkPrivileges(
              "ROLE_SERVICE_VEHICLETYPE_UNASSIGN",
            )}
            headerComponent={
              <Grid container spacing={2}>
                <Grid item xs={12} md={7} lg={5} className="mt-3">
                  <StyledInput
                    fullWidth
                    placeholder={t("title")}
                    handleSearchClick={handleSearchClick}
                    handleChange={handleInputChange}
                    value={search}
                    clearSearch={clearSearch}
                  />
                </Grid>
              </Grid>
            }
          />
        </div>
      </Box>
    </Box>
  );
}
